import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Loader2, ChevronUp, ChevronDown, X, Puzzle, Search } from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = { mc: "MC", sata: "SATA", task: "Task", likert: "Likert", cfi_mc: "CFI MC" };
const TYPE_COLORS = {
  mc: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  sata: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  task: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  likert: "bg-teal-500/15 text-teal-400 border-teal-500/20",
  cfi_mc: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
};
const CAT_COLORS = {
  survey: "bg-cyan-500/15 text-cyan-400 border-cyan-500/20",
  assessment: "bg-white/5 text-[#A1A9B8] border-white/10",
};

export default function ItemAssigner({ entityType, entityId, cfiMode = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerTypeFilter, setPickerTypeFilter] = useState("all");

  const isLexp = entityType === "lexp";
  const noun = isLexp ? "question" : "item";
  const nounPlural = isLexp ? "questions" : "items";
  const nounCap = isLexp ? "Question" : "Item";

  const baseUrl = isLexp
    ? `${API}/learning-experiences/${entityId}/items`
    : `${API}/assessment-types/${entityId}/items`;

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await axios.get(baseUrl, { withCredentials: true });
      setItems(data);
    } catch {
      toast.error(`Failed to load ${nounPlural}`);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, nounPlural]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openPicker = async () => {
    setShowPicker(true);
    setPickerSearch("");
    setPickerTypeFilter("all");
    setPickerLoading(true);
    try {
      const { data } = await axios.get(`${API}/items`, { withCredentials: true });
      setAllItems(data.filter((i) => i.status === "published"));
    } catch {
      toast.error(`Failed to load ${nounPlural}`);
    } finally {
      setPickerLoading(false);
    }
  };

  const handleAdd = async (itemId) => {
    try {
      await axios.post(baseUrl, { item_id: itemId }, { withCredentials: true });
      toast.success(`${nounCap} added`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const handleRemove = async (itemId) => {
    try {
      await axios.delete(`${baseUrl}/${itemId}`, { withCredentials: true });
      toast.success(`${nounCap} removed`);
      fetchItems();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const handleMove = async (idx, direction) => {
    const list = [...items];
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    [list[idx], list[targetIdx]] = [list[targetIdx], list[idx]];
    const newOrder = list.map((i) => i.id);
    try {
      await axios.put(`${baseUrl}/reorder`, { item_ids: newOrder }, { withCredentials: true });
      fetchItems();
    } catch {
      toast.error("Failed to reorder");
    }
  };

  const totalPoints = items.reduce((sum, i) => sum + (i.point_value || 0), 0);
  const assignedIds = new Set(items.map((i) => i.id));
  const assignedCfiMcCount = items.filter((i) => i.item_type === "cfi_mc").length;

  // Picker filtering. CFI mode: only CFI MC items, capped at 1. Non-CFI LEXPs:
  // hide CFI MC items entirely. Always hide system-managed Likert from the picker.
  const filteredPickerItems = allItems.filter((i) => {
    if (i.system_managed) return false;
    if (isLexp) {
      if (cfiMode) {
        if (i.item_type !== "cfi_mc") return false;
        if (assignedCfiMcCount >= 1 && !assignedIds.has(i.id)) return false;
      } else {
        if (i.item_type === "cfi_mc") return false;
      }
    }
    if (pickerTypeFilter !== "all" && i.item_type !== pickerTypeFilter) return false;
    if (pickerSearch.trim()) {
      const q = pickerSearch.toLowerCase();
      const stem = (i.stem || "").replace(/<[^>]*>/g, "").toLowerCase();
      // topic_tag may be a string, an array of tag ids, or missing — normalize before matching
      const tagStr = Array.isArray(i.topic_tag)
        ? i.topic_tag.join(" ").toLowerCase()
        : String(i.topic_tag || "").toLowerCase();
      if (!(i.title || "").toLowerCase().includes(q) && !stem.includes(q) && !tagStr.includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`item-assigner-${entityType}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-body font-bold text-base text-white">Assigned {isLexp ? "Questions" : "Items"}</h3>
          {items.length > 0 && (
            <p className="text-xs text-[#6E7787] font-body mt-0.5">
              {items.length} {items.length !== 1 ? nounPlural : noun} — {totalPoints} total points
            </p>
          )}
        </div>
        <Button onClick={openPicker} data-testid="add-item-btn"
          className="h-9 px-4 font-body text-sm font-medium text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
        >
          <Plus className="h-4 w-4 mr-1.5" />Add {nounCap}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-10 text-center" data-testid="items-empty">
          <Puzzle className="h-8 w-8 text-[#6E7787] mx-auto mb-2" />
          <p className="text-sm text-[#6E7787] font-body">No {nounPlural} assigned yet.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="assigned-items-list">
          {items.map((item, idx) => (
            <div key={item.id} className="bg-[#11141A] border border-white/10 rounded-lg px-4 py-3 flex items-center gap-3 hover:border-white/20 transition-colors" data-testid={`assigned-item-${item.id}`}>
              <div className="flex flex-col items-center gap-0.5 shrink-0">
                <button onClick={() => handleMove(idx, "up")} disabled={idx === 0} className="text-[#6E7787] hover:text-white disabled:opacity-30 p-0.5" data-testid={`move-up-${item.id}`}>
                  <ChevronUp className="h-4 w-4" />
                </button>
                <span className="text-xs text-[#6E7787] font-body w-5 text-center">{idx + 1}</span>
                <button onClick={() => handleMove(idx, "down")} disabled={idx === items.length - 1} className="text-[#6E7787] hover:text-white disabled:opacity-30 p-0.5" data-testid={`move-down-${item.id}`}>
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-body font-medium text-white truncate">{item.title}</p>
                <p className="text-xs text-[#6E7787] font-body truncate mt-0.5">{(item.stem || "").replace(/<[^>]*>/g, "").substring(0, 80)}</p>
              </div>
              <Badge className={`text-xs font-body shrink-0 ${TYPE_COLORS[item.item_type] || ""}`}>
                {TYPE_LABELS[item.item_type] || item.item_type}
              </Badge>
              <Badge className={`text-xs font-body shrink-0 ${CAT_COLORS[item.item_category] || CAT_COLORS.assessment}`}>
                {item.item_category === "survey" ? "Survey" : "Assessment"}
              </Badge>
              {Array.isArray(item.topic_tag) ? (
                item.topic_tag.length > 0 && (
                  <span className="text-xs text-[#6E7787] font-body hidden sm:block shrink-0">{item.topic_tag.length} tag{item.topic_tag.length !== 1 ? "s" : ""}</span>
                )
              ) : item.topic_tag ? (
                <span className="text-xs text-[#6E7787] font-body hidden sm:block shrink-0">{item.topic_tag}</span>
              ) : null}
              <span className="text-xs text-white font-body font-medium shrink-0">{item.point_value}pt</span>
              <Button variant="ghost" size="sm" onClick={() => handleRemove(item.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2 shrink-0" data-testid={`remove-item-${item.id}`}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Item Picker Dialog */}
      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg max-h-[80vh] flex flex-col" data-testid="item-picker-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">Add {nounCap}</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">Search and select a published {noun} to assign.</DialogDescription>
          </DialogHeader>

          {/* Search & filter */}
          <div className="space-y-2 pb-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#6E7787]" />
              <Input
                value={pickerSearch}
                onChange={(e) => setPickerSearch(e.target.value)}
                placeholder={`Search ${nounPlural} by title, stem, or tag...`}
                data-testid="picker-search-input"
                className="pl-8 bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-9 text-sm"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {(cfiMode ? ["all", "cfi_mc"] : ["all", "mc", "sata", "task", "likert"]).map((t) => (
                <button key={t} onClick={() => setPickerTypeFilter(t)} data-testid={`picker-filter-${t}`}
                  className={`px-3 py-1 text-xs font-body rounded-md border transition-colors ${pickerTypeFilter === t ? "border-white/20 bg-white/10 text-white" : "border-white/10 text-[#6E7787] hover:text-white"}`}
                >
                  {t === "all" ? "All" : TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          {/* Results */}
          <div className="overflow-y-auto flex-1 min-h-0">
            {pickerLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
            ) : filteredPickerItems.length === 0 ? (
              <p className="text-[#6E7787] font-body text-sm py-6 text-center">
                {allItems.length === 0 ? `No published ${nounPlural} available.` : `No ${nounPlural} match your search.`}
              </p>
            ) : (
              <div className="space-y-2 mt-2">
                {filteredPickerItems.map((item) => {
                  const added = assignedIds.has(item.id);
                  return (
                    <div key={item.id} className={`border border-white/10 rounded-lg p-3 transition-all ${added ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-white/20"}`}
                      onClick={() => !added && handleAdd(item.id)} data-testid={`picker-item-${item.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-xs ${TYPE_COLORS[item.item_type]}`}>{TYPE_LABELS[item.item_type]}</Badge>
                            <Badge className={`text-xs ${CAT_COLORS[item.item_category] || CAT_COLORS.assessment}`}>
                              {item.item_category === "survey" ? "Survey" : "Assessment"}
                            </Badge>
                            <h4 className="text-sm font-body font-medium text-white truncate">{item.title}</h4>
                          </div>
                          <p className="text-xs text-[#6E7787] font-body mt-0.5 truncate">{(item.stem || "").replace(/<[^>]*>/g, "").substring(0, 60)}</p>
                        </div>
                        {added ? (
                          <Badge className="bg-white/5 text-[#6E7787] border-white/10 shrink-0">Added</Badge>
                        ) : (
                          <span className="text-xs text-[#6E7787] font-body shrink-0">{item.point_value}pt</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
